import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  Save as SaveIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';

interface Product {
  id: number;
  sku: string;
  name: string;
  category_name: string;
  category_id: number;
  unit_size: string;
  qty_in_box: number;
  unit_type: string;
  image_url: string;
  stock_quantity: number;
  stock_single: number;
}

interface Category {
  id: number;
  name: string;
}

interface ShipmentItem {
  id?: number;
  product_id?: number;
  product?: Product;
  expected_box_quantity: number;
  expected_single_quantity: number;
  is_new_product: boolean;
  new_product_data?: any;
  notes?: string;
}

interface ShipmentData {
  from_location: string;
  expected_date: string;
  status: string;
  notes: string;
  items: ShipmentItem[];
}

const ShipmentForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, hasPermission, hasRole } = useAuth();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState<ShipmentData>({
    from_location: '',
    expected_date: '',
    status: 'pending',
    notes: '',
    items: []
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [newProductDialogOpen, setNewProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | ''>('');
  const [tempQuantities, setTempQuantities] = useState<{ [key: number]: { box: number; single: number } }>({});

  // New product form data
  const [newProductData, setNewProductData] = useState({
    sku: '',
    name: '',
    description: '',
    category_id: '',
    unit_size: '',
    qty_in_box: '',
    unit_type: ''
  });

  // Unit type management for new product form
  const [unitTypes, setUnitTypes] = useState<string[]>([]);
  const [isTypingNewUnit, setIsTypingNewUnit] = useState(false);
  const [customUnitType, setCustomUnitType] = useState('');

  // Default fallback unit types (in case API fails)
  const defaultUnitTypes = [
    'Piece',
    'Pack', 
    'Box',
    'Bundle',
    'Bottle',
  ];

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchUnitTypes();
    if (isEdit) {
      fetchShipment();
    }
  }, [id]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/shipments/products');
      if (response.data.success) {
        setProducts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchUnitTypes = async () => {
    try {
      const response = await api.get('/products/unit-types');
      if (response.data && response.data.success) {
        setUnitTypes(response.data.data);
      } else {
        // Fall back to default unit types if API call fails
        console.warn('No unit types data received from API, using default unit types');
        setUnitTypes(defaultUnitTypes);
      }
    } catch (error) {
      console.warn('Failed to fetch unit types, using default unit types:', error);
      setUnitTypes(defaultUnitTypes);
    }
  };

  const fetchShipment = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/shipments/${id}`);
      if (response.data.success) {
        const shipment = response.data.data;
        setFormData({
          from_location: shipment.from_location,
          expected_date: shipment.expected_date || '',
          status: shipment.status,
          notes: shipment.notes || '',
          items: shipment.items.map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            product: item.product_id ? {
              id: item.product_id,
              sku: item.sku,
              name: item.product_name,
              category_name: item.category_name,
              unit_size: item.unit_size,
              qty_in_box: item.qty_in_box,
              image_url: item.image_url
            } : null,
            expected_box_quantity: item.expected_box_quantity,
            expected_single_quantity: item.expected_single_quantity,
            is_new_product: item.is_new_product,
            new_product_data: item.new_product_data ? JSON.parse(item.new_product_data) : null,
            notes: item.notes
          }))
        });
      }
    } catch (error) {
      setError('Failed to fetch shipment');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ShipmentData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOpenProductDialog = () => {
    // Initialize temp quantities with existing shipment items
    const initialTempQuantities: { [key: number]: { box: number; single: number } } = {};
    formData.items.forEach(item => {
      if (item.product_id) {
        initialTempQuantities[item.product_id] = {
          box: item.expected_box_quantity,
          single: item.expected_single_quantity
        };
      }
    });
    setTempQuantities(initialTempQuantities);
    setProductDialogOpen(true);
  };

  const handleCloseProductDialog = () => {
    // Clear temp quantities when dialog closes
    setTempQuantities({});
    setProductDialogOpen(false);
  };

  const handleQuantityChange = (index: number, field: 'expected_box_quantity' | 'expected_single_quantity', value: number) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = Math.max(0, value);
    
    // Check if both quantities are 0 after this change
    const item = updatedItems[index];
    if (item.expected_box_quantity === 0 && item.expected_single_quantity === 0) {
      // Remove item if both quantities are 0
      const filteredItems = updatedItems.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, items: filteredItems }));
    } else {
      setFormData(prev => ({ ...prev, items: updatedItems }));
    }
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  // Unit type handlers for new product form
  const handleUnitTypeChange = (e: any) => {
    const value = e.target.value;
    if (value === '___TYPE_NEW_UNIT___') {
      setIsTypingNewUnit(true);
      setCustomUnitType('');
      setNewProductData(prev => ({ ...prev, unit_type: '' }));
    } else {
      setIsTypingNewUnit(false);
      setCustomUnitType('');
      setNewProductData(prev => ({ ...prev, unit_type: value }));
    }
  };

  const handleCustomUnitTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomUnitType(e.target.value);
  };

  const handleCreateNewProduct = async () => {
    try {
      // Validate required fields
      if (!newProductData.sku || !newProductData.name) {
        setError('SKU and name are required for new products');
        return;
      }

      // Validate unit type
      const unitTypeToSend = isTypingNewUnit ? customUnitType : newProductData.unit_type;
      if (!unitTypeToSend) {
        setError('Unit type is required for new products');
        return;
      }

      // Create the product with the correct unit type
      const productDataToSend = {
        ...newProductData,
        unit_type: unitTypeToSend
      };
      const response = await api.post('/shipments/products', productDataToSend);
      if (response.data.success) {
        const newProduct = response.data.data;
        
        // Add to products list
        setProducts(prev => [...prev, newProduct]);
        
        // Add to shipment items with default quantity
        const newItem: ShipmentItem = {
          product_id: newProduct.id,
          product: newProduct,
          expected_box_quantity: 1,
          expected_single_quantity: 0,
          is_new_product: false,
          notes: ''
        };
        setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
        
        // Reset form and close dialog
        setNewProductData({
          sku: '',
          name: '',
          description: '',
          category_id: '',
          unit_size: '',
          qty_in_box: '',
          unit_type: ''
        });
        setIsTypingNewUnit(false);
        setCustomUnitType('');
        setNewProductDialogOpen(false);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to create product');
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate form
      if (!formData.from_location) {
        setError('From location is required');
        return;
      }

      if (formData.items.length === 0) {
        setError('At least one product is required');
        return;
      }

      const submitData = {
        ...formData,
        items: formData.items.map(item => ({
          product_id: item.product_id,
          expected_box_quantity: item.expected_box_quantity,
          expected_single_quantity: item.expected_single_quantity,
          is_new_product: item.is_new_product,
          new_product_data: item.new_product_data,
          notes: item.notes
        }))
      };

      let response;
      if (isEdit) {
        response = await api.put(`/shipments/${id}`, submitData);
      } else {
        response = await api.post('/shipments', submitData);
      }

      if (response.data.success) {
        navigate('/shipments');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save shipment');
    } finally {
      setSaving(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !categoryFilter || product.category_id === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate('/shipments')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Shipment' : 'Create New Shipment'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Basic Information */}
        <Box mb={4}>
          <Box 
            display="flex" 
            flexDirection={{ xs: 'column', md: 'row' }} 
            gap={3}
            mb={3}
          >
            <Box flex={1}>
              <TextField
                fullWidth
                label="From Location *"
                value={formData.from_location}
                onChange={(e) => handleInputChange('from_location', e.target.value)}
                placeholder="e.g., Production Facility A"
              />
            </Box>
            <Box flex={1}>
              <TextField
                fullWidth
                label="Expected Date"
                type="date"
                value={formData.expected_date}
                onChange={(e) => handleInputChange('expected_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box flex={1}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_transit">In Transit</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes about this shipment..."
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Products Section */}
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Products</Typography>
            <Box display="flex" gap={1}>
              {(hasPermission('products.create_limited') || hasRole(['super_admin', 'admin'])) && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setNewProductDialogOpen(true)}
                >
                  New Product
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenProductDialog}
              >
                Add Product
              </Button>
            </Box>
          </Box>

          {/* Items Table */}
          {formData.items.length > 0 ? (
            <TableContainer sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Image</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell align="center">Box Qty</TableCell>
                    <TableCell align="center">Single Qty</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.product?.category_name}</TableCell>
                      <TableCell>
                        <Box
                          component="img"
                          src={getImageUrl(item.product?.image_url)}
                          alt={item.product?.name}
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell>{item.product?.sku}</TableCell>
                      <TableCell>{item.product?.name}</TableCell>
                      <TableCell>
                        {item.product?.unit_size && item.product?.qty_in_box 
                          ? `${item.product.unit_size} x ${item.product.qty_in_box}`
                          : item.product?.unit_size || '-'
                        }
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(index, 'expected_box_quantity', item.expected_box_quantity - 1)}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={item.expected_box_quantity}
                            onChange={(e) => handleQuantityChange(index, 'expected_box_quantity', Math.max(0, parseInt(e.target.value) || 0))}
                            onFocus={(e) => e.target.select()}
                            inputProps={{ 
                              min: 0,
                              style: { textAlign: 'center', width: '50px' }
                            }}
                            sx={{ 
                              mx: 0.5,
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: '1px solid rgba(0, 0, 0, 0.23)' },
                                '&.Mui-focused fieldset': { border: '2px solid #1976d2' }
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(index, 'expected_box_quantity', item.expected_box_quantity + 1)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(index, 'expected_single_quantity', item.expected_single_quantity - 1)}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={item.expected_single_quantity}
                            onChange={(e) => handleQuantityChange(index, 'expected_single_quantity', Math.max(0, parseInt(e.target.value) || 0))}
                            onFocus={(e) => e.target.select()}
                            inputProps={{ 
                              min: 0,
                              style: { textAlign: 'center', width: '50px' }
                            }}
                            sx={{ 
                              mx: 0.5,
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: '1px solid rgba(0, 0, 0, 0.23)' },
                                '&.Mui-focused fieldset': { border: '2px solid #1976d2' }
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(index, 'expected_single_quantity', item.expected_single_quantity + 1)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              No products added yet. Click "Add Product" to start adding items to this shipment.
            </Alert>
          )}
        </Box>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
          <Button variant="outlined" onClick={() => navigate('/shipments')}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving...' : isEdit ? 'Update Shipment' : 'Create Shipment'}
          </Button>
        </Box>
      </Paper>

      {/* Product Selection Dialog */}
      <Dialog open={productDialogOpen} onClose={handleCloseProductDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Select Products</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            {(searchTerm || categoryFilter) && ` (filtered from ${products.length} total)`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Filters */}
          <Box display="flex" gap={2} mb={3}>
            <TextField
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1 }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value as number)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Compact Products Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="center">Box Qty</TableCell>
                  <TableCell align="center">Single Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => {
                  const existingItem = formData.items.find(item => item.product_id === product.id);
                  const tempBoxQty = tempQuantities[product.id]?.box ?? existingItem?.expected_box_quantity ?? 0;
                  const tempSingleQty = tempQuantities[product.id]?.single ?? existingItem?.expected_single_quantity ?? 0;
                  
                  const setTempBoxQty = (value: number) => {
                    const newSingleQty = tempQuantities[product.id]?.single ?? existingItem?.expected_single_quantity ?? 0;
                    setTempQuantities(prev => ({
                      ...prev,
                      [product.id]: { ...prev[product.id], box: value, single: newSingleQty }
                    }));
                    // Real-time update
                    handleQuantityUpdate(value, newSingleQty);
                  };
                  
                  const setTempSingleQty = (value: number) => {
                    const newBoxQty = tempQuantities[product.id]?.box ?? existingItem?.expected_box_quantity ?? 0;
                    setTempQuantities(prev => ({
                      ...prev,
                      [product.id]: { ...prev[product.id], box: newBoxQty, single: value }
                    }));
                    // Real-time update
                    handleQuantityUpdate(newBoxQty, value);
                  };
                  
                  const handleQuantityUpdate = (boxQty: number, singleQty: number) => {
                    const existingItemIndex = formData.items.findIndex(item => item.product_id === product.id);
                    
                    if (boxQty === 0 && singleQty === 0) {
                      // Remove item if both quantities are 0
                      if (existingItemIndex >= 0) {
                        const updatedItems = formData.items.filter((_, i) => i !== existingItemIndex);
                        setFormData(prev => ({ ...prev, items: updatedItems }));
                      }
                    } else {
                      if (existingItemIndex >= 0) {
                        // Update existing item
                        const updatedItems = [...formData.items];
                        updatedItems[existingItemIndex].expected_box_quantity = boxQty;
                        updatedItems[existingItemIndex].expected_single_quantity = singleQty;
                        setFormData(prev => ({ ...prev, items: updatedItems }));
                      } else {
                        // Add new item
                        const newItem: ShipmentItem = {
                          product_id: product.id,
                          product,
                          expected_box_quantity: boxQty,
                          expected_single_quantity: singleQty,
                          is_new_product: false,
                          notes: ''
                        };
                        setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
                      }
                    }
                  };

                  return (
                    <TableRow 
                      key={product.id}
                      sx={{ 
                        backgroundColor: existingItem ? 'action.selected' : 'inherit',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {product.category_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="img"
                          src={getImageUrl(product.image_url)}
                          alt={product.name}
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {product.sku}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {product.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {product.unit_size && product.qty_in_box 
                            ? `${product.unit_size} x ${product.qty_in_box}`
                            : product.unit_size || '-'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => setTempBoxQty(Math.max(0, tempBoxQty - 1))}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={tempBoxQty}
                            onChange={(e) => setTempBoxQty(Math.max(0, parseInt(e.target.value) || 0))}
                            onFocus={(e) => e.target.select()}
                            inputProps={{ 
                              min: 0,
                              style: { textAlign: 'center', width: '50px' }
                            }}
                            sx={{ 
                              mx: 0.5,
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: '1px solid rgba(0, 0, 0, 0.23)' },
                                '&.Mui-focused fieldset': { border: '2px solid #1976d2' }
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setTempBoxQty(tempBoxQty + 1)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => setTempSingleQty(Math.max(0, tempSingleQty - 1))}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={tempSingleQty}
                            onChange={(e) => setTempSingleQty(Math.max(0, parseInt(e.target.value) || 0))}
                            onFocus={(e) => e.target.select()}
                            inputProps={{ 
                              min: 0,
                              style: { textAlign: 'center', width: '50px' }
                            }}
                            sx={{ 
                              mx: 0.5,
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: '1px solid rgba(0, 0, 0, 0.23)' },
                                '&.Mui-focused fieldset': { border: '2px solid #1976d2' }
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setTempSingleQty(tempSingleQty + 1)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProductDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* New Product Dialog */}
      <Dialog 
        open={newProductDialogOpen} 
        onClose={() => {
          setNewProductDialogOpen(false);
          setIsTypingNewUnit(false);
          setCustomUnitType('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Create New Product</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Box 
              display="flex" 
              flexDirection={{ xs: 'column', sm: 'row' }} 
              gap={2}
              mb={2}
            >
              <Box flex={1}>
                <TextField
                  fullWidth
                  label="SKU *"
                  value={newProductData.sku}
                  onChange={(e) => setNewProductData(prev => ({ ...prev, sku: e.target.value }))}
                />
              </Box>
              <Box flex={1}>
                <TextField
                  fullWidth
                  label="Product Name *"
                  value={newProductData.name}
                  onChange={(e) => setNewProductData(prev => ({ ...prev, name: e.target.value }))}
                />
              </Box>
            </Box>
            <Box mb={2}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={newProductData.description}
                onChange={(e) => setNewProductData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Box>
            <Box 
              display="flex" 
              flexDirection={{ xs: 'column', sm: 'row' }} 
              gap={2}
              mb={2}
            >
              <Box flex={1}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newProductData.category_id}
                    label="Category"
                    onChange={(e) => setNewProductData(prev => ({ ...prev, category_id: e.target.value }))}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box flex={1}>
                <TextField
                  fullWidth
                  label="Unit Size"
                  value={newProductData.unit_size}
                  onChange={(e) => setNewProductData(prev => ({ ...prev, unit_size: e.target.value }))}
                  placeholder="e.g., 1kg, 500ml"
                />
              </Box>
            </Box>
            <Box 
              display="flex" 
              flexDirection={{ xs: 'column', sm: 'row' }} 
              gap={2}
            >
              <Box flex={1}>
                <TextField
                  fullWidth
                  label="Quantity in Box"
                  type="number"
                  value={newProductData.qty_in_box}
                  onChange={(e) => setNewProductData(prev => ({ ...prev, qty_in_box: e.target.value }))}
                />
              </Box>
              <Box flex={1}>
                {isTypingNewUnit ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      label="New Unit Type"
                      value={customUnitType}
                      onChange={handleCustomUnitTypeChange}
                      placeholder="e.g., Pack, Pcs, Bottle"
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIsTypingNewUnit(false);
                        setCustomUnitType('');
                        setNewProductData(prev => ({ ...prev, unit_type: '' }));
                      }}
                      sx={{ minWidth: 'auto', height: '56px' }}
                    >
                      ✕
                    </Button>
                  </Box>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Unit Type</InputLabel>
                    <Select
                      value={newProductData.unit_type}
                      label="Unit Type"
                      onChange={handleUnitTypeChange}
                    >
                      <MenuItem value="___TYPE_NEW_UNIT___" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                        + Type new unit
                      </MenuItem>
                      {unitTypes.map((unitType) => (
                        <MenuItem key={unitType} value={unitType}>
                          {unitType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewProductDialogOpen(false);
            setIsTypingNewUnit(false);
            setCustomUnitType('');
          }}>Cancel</Button>
          <Button onClick={handleCreateNewProduct} variant="contained">
            Create Product
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShipmentForm;